import { FirebaseApp } from 'firebase/app';
import {
  initializeAppCheck,
  ReCaptchaEnterpriseProvider,
} from 'firebase/app-check';
import {
  Auth,
  getAuth,
  getIdTokenResult,
  IdTokenResult,
  ParsedToken,
  signInWithEmailAndPassword,
  signOut,
  User,
} from 'firebase/auth';
import {
  collection,
  CollectionReference,
  doc,
  Firestore,
  getFirestore,
  initializeFirestore,
} from 'firebase/firestore';
import { getFunctions } from 'firebase/functions';
import { getBlob, getStorage, ref } from 'firebase/storage';
import isEmpty from 'lodash/isEmpty';
import { authState } from 'rxfire/auth';
import { collectionData, docData } from 'rxfire/firestore';
import {
  BehaviorSubject,
  EMPTY,
  from,
  Observable,
  of,
} from 'rxjs';
import { catchError, filter, first, map, switchMap, take, tap } from 'rxjs/operators';
import { IOrganization, IPermission, IUser } from '../types';
import { tenantApp$, getRegionConfig, IAppServices, clearTenantConfig } from './appsProvider';

/**
 * @description always on listener for auth state changes from the tenant app
 * The tenant app is dynamically initialized after the identity service returns config
 * This stream is never disposed unless page is refreshed/closed
 */
const authenticatedUserStream = new BehaviorSubject<[User, IAppServices]>(null);

tenantApp$
  .pipe(
    switchMap((app: FirebaseApp | null): Observable<[User, IAppServices]> => {
      if (!app) return of(null);

      const auth = getAuth(app);
      return authState(auth).pipe(
        map((user: User): [User, IAppServices] => {
          if (user) {
            const appServices = initializeServices(app);
            return [user, appServices];
          }
          // Reset user not provisioned flag when user signs out
          userNotProvisioned$.next(false);
          return null;
        })
      );
    })
  )
  .subscribe(authenticatedUserStream);

/**
 * @description user stream
 */
const user$ = new BehaviorSubject<IUser>(null);

/**
 * @description user not provisioned error state
 */
const userNotProvisioned$ = new BehaviorSubject<boolean>(false);

/**
 * @description wire up and start the flow
 */
authenticatedUserStream
  .pipe(
    /// only emit valid user
    filter(
      (evt: [User, IAppServices]): boolean => !!evt && !!evt[0] && !!evt[1]
    ),
    tap((evt: [User, IAppServices]): void => {
      // eslint-disable-next-line @typescript-eslint/dot-notation
      if (window['TrackJS']) {
        // eslint-disable-next-line @typescript-eslint/dot-notation
        window['TrackJS'].configure({
          userId: evt[0].uid,
        });
      }
    }),
    /// get any claims the user has from the firebase auth jwt token
    switchMap((evt: [User, IAppServices]): Observable<IUser> => {
      const [user, appServices] = evt;
      return from(getIdTokenResult(user)).pipe(
        map((token: IdTokenResult): ParsedToken => ({ ...token.claims })),
        /// get the user profile from the database
        switchMap((tokenResult: ParsedToken): Observable<IUser> => {
          return profile(tokenResult, appServices);
        })
      );
    }),
    switchMap(
      (user: IUser): Observable<Partial<IUser>> => teamPermissions(user)
    ),
    /// get the users team permissions from the database

    map((user: Partial<IUser>): IUser => {
      /// generate a list of team ids this user is the admin of
      const adminOf = user?.permissions
        ?.filter((perm: IPermission): boolean => perm.write)
        .map((perm: IPermission): string => perm.id);
      return {
        ...user,
        adminOf,
        services: user.services,
      };
    }),
    tap((user: IUser): void => {
      // Check if user is not an org admin and has no team access
      const isOrgAdmin = user?.claims?.orgAdmin || user?.claims?.hawkin;
      const hasTeamAccess = user?.permissions && user.permissions.length > 0;

      if (!isOrgAdmin && !hasTeamAccess) {
        console.error('User has no team access and is not an org admin - user not provisioned');
        throw new Error('USER_NOT_PROVISIONED');
      }
    }),
    catchError((error) => {
      console.error('Error in user stream:', error);
      if (error?.message === 'USER_NOT_PROVISIONED') {
        userNotProvisioned$.next(true);
        return EMPTY;
      }
      throw error;
    })
  )
  .subscribe(user$);

/**
 * @description returns a hot observable of the user (emits both an authenticated user or null/undefined if
 * 	the user has not authenticated
 */
const userProvider = (): Observable<IUser> => user$;

/**
 * @description returns a hot observable of the user not provisioned state
 */
const userNotProvisionedProvider = (): Observable<boolean> => userNotProvisioned$;

/**
 * @description returns a hot observable of the user (emits the autheticated user only)
 * @returns
 */
const authenticatedUserProvider = (): Observable<IUser> =>
  user$.pipe(filter((user: IUser): boolean => !isEmpty(user)));

/**
 * @description returns a promise for the currently logged in user
 */
const userAsPromise = (): Promise<IUser> =>
  user$.pipe(first((x: IUser): boolean => !isEmpty(x))).toPromise();

/**
 * @description retrieves the users team permissions from firestore
 */
const teamPermissions = (
  user: IUser
): Observable<never> | Observable<Partial<IUser>> => {
  if (isEmpty(user)) return EMPTY;
  return collectionData(
    collection(
      user.services.firestore,
      `userProfiles/${user.id}/teams`
    ) as CollectionReference<IPermission>,
    { idField: 'id' }
  ).pipe(
    map(
      (permissions: IPermission[]): Partial<IUser> => ({
        ...user,
        permissions,
      })
    )
  );
};

// ParsedToken type does not allow boolean values for claims which is valid, allow any for now
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const profile = (claims: any, appServices: IAppServices): Observable<IUser> => {
  if (isEmpty(claims)) return EMPTY;

  return docData(doc(appServices.firestore, `userProfiles/${claims.sub}`), {
    idField: 'id',
  }).pipe(
    catchError((error) => {
      console.error('Error reading user profile:', error);
      // Check if this is a permission error (user not provisioned)
      if (error?.code === 'permission-denied') {
        console.error('Permission denied - user not provisioned');
        throw new Error('USER_NOT_PROVISIONED');
      }
      throw error;
    }),
    map((mProfile: IUser): IUser => {
      return {
        ...mProfile,
        services: appServices,
        claims: {
          hawkin: claims.hawkin,
          orgAdmin: claims.orgAdmin || claims.hawkin,
          orgId: claims.orgId,
        },
      };
    }),
    switchMap((mProfile: IUser): Observable<IUser> => {
      // Check if user has been provisioned with an organization
      if (!mProfile.orgId || isEmpty(mProfile.orgId)) {
        console.error('User profile exists but has no orgId - user not provisioned');
        throw new Error('USER_NOT_PROVISIONED');
      }

      const org$ = docData(
        doc(appServices.firestore, `orgs/${mProfile.orgId}`),
        { idField: 'id' }
      );
      return org$.pipe(
        catchError((error) => {
          console.error('Error fetching org:', error);
          // If org fetch fails with permission denied, user is not properly provisioned
          if (error?.code === 'permission-denied') {
            console.error('Permission denied fetching org - user not provisioned');
            throw new Error('USER_NOT_PROVISIONED');
          }
          throw error;
        }),
        map((org: IOrganization): IUser => {
          return {
            ...mProfile,
            orgOptions: org.options,
            orgV1NamedEndpoint: org.v1NamedEndpoint,
          };
        })
      );
    })
  );
};

/**
 * @description synchronous check of tenant app to retrieve the current user
 */
export const currentUser = (): User => {
  const tenant = tenantApp$.value;
  return tenant ? getAuth(tenant).currentUser : null;
};

/**
 * @description emits when tenant app auth onAuthStateChange has emitted once
 * @returns
 */
export const authCheck = (): Observable<null> => {
  const tenant = tenantApp$.value;
  if (!tenant) return of(null);

  const auth = getAuth(tenant);
  return authState(auth).pipe(
    take(1),
    map((_: User): null => null)
  );
};

/**
 * @description synchronous check of authentication status
 * @returns
 */
export const isAuthenticated = (): boolean => Boolean(currentUser());

/**
 * @description attempts login with the given credentials
 * @param email
 * @param password
 * @param auth - Firebase Auth instance (pre-initialized with correct tenant context)
 * @returns
 */
export const authenticate = async (
  email: string,
  password: string,
  auth: Auth
): Promise<void> => {
  try {
    await signInWithEmailAndPassword(auth, email, password);
    // The authenticatedUserStream will automatically pick up the auth state change
    // because it subscribes to the tenant app which this auth belongs to
  } catch (err) {
    const authError = err as { code?: string };
    // Don't log MFA required error - it's expected behavior
    if (authError.code !== 'auth/multi-factor-auth-required') {
      console.error('authenticate error:', err);
    }
    throw err;
  }
};

/**
 * @description logs out of the tenant app and reloads the page
 */
export const unauthenticate = async (): Promise<void> => {
  // Clear stored tenant config to prevent auto-login on next page load
  clearTenantConfig();

  const tenant = tenantApp$.value;
  if (tenant) {
    try {
      const auth = getAuth(tenant);
      await signOut(auth);
    } catch (e) {
      console.error('Error signing out:', e);
    }
  }
  window.location.replace('/');
};

/**
 * @description initializes the services for the given app
 * @param app
 * @returns
 */
const initializeServices = (app: FirebaseApp): IAppServices => {
  const region = getRegionConfig(app);
  let firestore: Firestore;
  try {
    firestore = initializeFirestore(app, {
      experimentalForceLongPolling: true,
    });
  } catch (e) {
    firestore = getFirestore(app);
  }
  const storage = getStorage(app);

  // App Check is now initialized in getAuthForTenant before authentication
  // Only initialize for apps that don't have it yet (like the global app)
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(region.siteKey),
      isTokenAutoRefreshEnabled: true,
    });
  } catch (e) {
    // App Check likely already initialized - this is expected for tenant apps
  }

  return {
    app,
    firestore,
    auth: getAuth(app),
    storage,
    functions: getFunctions(app, region.domain),
    getGCSImage: (filename: string): Promise<Blob> => {
      const file = ref(storage, `gs://${region.userImageBucket}/${filename}`);
      return getBlob(file);
    },
  };
};

export { authenticatedUserProvider, userAsPromise, userProvider, userNotProvisionedProvider };
